import { useMentorStories } from "@/lib/hooks/mentor-stories/use-mentor-stories";
import { useMentorStoriesEnabled } from "@/lib/hooks/use-mentor-stories";
import { FirstMomentsAccordion } from "./first-moments-accordion";
import { StorySection } from "./stories/story-section";
import { FirstMomentsMessages } from "./first-moments-messages";
import { MessageContext } from "@/lib/api/onboarding-messages";
import { useMessages } from "@/lib/hooks/onboarding-messages/use-onboarding-messages";
import { onboardingMessagesForDisplay } from "./first-moments-summaries";
import { Segmented } from "@somethings-ui/web";
import { useState } from "react";

interface FirstMomentsTabProps {
  userId: string;
}

export function FirstMomentsTab({ userId }: FirstMomentsTabProps) {
  const storiesEnabled = useMentorStoriesEnabled();
  const [messagesOpen, setMessagesOpen] = useState(true);
  const [context, setContext] = useState<MessageContext>(MessageContext.ONLINE);
  const onlineQuery = useMessages(userId, MessageContext.ONLINE);
  const offlineQuery = useMessages(userId, MessageContext.OFFLINE);
  const onlineCount = onboardingMessagesForDisplay(
    onlineQuery.isSuccess ? (onlineQuery.data ?? []) : undefined,
    MessageContext.ONLINE,
  ).messages.length;
  const { data: story } = useMentorStories(userId, storiesEnabled);
  const offlineCount = onboardingMessagesForDisplay(
    offlineQuery.isSuccess ? (offlineQuery.data ?? []) : undefined,
    MessageContext.OFFLINE,
  ).messages.length;

  return (
    <section className="max-w-[1060px]">
      <h2 className="font-heading text-header-5 text-text-primary">
        First Moments
      </h2>
      <p className="mt-xs font-body text-body-3 font-medium text-text-tertiary">
        What teens get the first time they open your chat - your first messages,
        then your story.
      </p>

      <div className="mt-2xl flex flex-col gap-sm">
        <FirstMomentsAccordion
          title="First messages"
          expandedHint="Sent automatically when a teen opens your chat for the first time."
          open={messagesOpen}
          onToggle={() => setMessagesOpen((open) => !open)}
        >
          <div className="flex flex-col gap-lg">
            <Segmented
              aria-label="First messages"
              value={context}
              onChange={(value) => {
                if (
                  value === MessageContext.ONLINE ||
                  value === MessageContext.OFFLINE
                ) {
                  setContext(value);
                }
              }}
              options={[
                {
                  label: `Online (${onlineCount})`,
                  value: MessageContext.ONLINE,
                },
                {
                  label: `Offline (${offlineCount})`,
                  value: MessageContext.OFFLINE,
                },
              ]}
            />
            <FirstMomentsMessages
              key={context}
              userId={userId}
              context={context}
            />
          </div>
        </FirstMomentsAccordion>
        {storiesEnabled ? (
          <StorySection userId={userId} publishedImages={story?.images ?? []} />
        ) : null}
      </div>
    </section>
  );
}
